import TextValidator from '../shared/textValidator'
import { ProfileRepository } from './repository'
import { Itinerary2, ItineraryStatus } from '~/models/Itinerary'
import { Page } from '~/models/PaginatedItems'
import { Profile } from '~/models/Profile'
import Traveler from '~/models/Traveler'
import { ProfileDestination, ProfileDestinationType } from '~/models/ProfileDestination'
import { ProfileStats } from '~/models/ProfileStats'
import { Destination2 } from '~/models/Destination'
export class ProfileService {
	static async getProfile(id: string): Promise<Profile> {
		return await ProfileRepository.getProfile(id)
	}

	static async getTraveler(id: string): Promise<Traveler> {
		return await ProfileRepository.getTraveler(id)
	}

	static async updateProfile(id: string, profile: Profile): Promise<Profile> {
		return await ProfileRepository.updateProfile(id, profile)
	}

	static async getItineraries({
		id,
		page = 0,
		status = 'all',
		destinationId = '',
	}: {
		id: string
		page?: number
		status?: ItineraryStatus
		destinationId?: string
	}): Promise<Page<Itinerary2>> {
		return await ProfileRepository.getItineraries({
			id,
			page,
			status,
			destinationId,
		})
	}

	static async addDestinations(
		userId: string,
		destinations: ProfileDestination[]
	): Promise<ProfileDestination[]> {
		return await ProfileRepository.addDestinations(userId, destinations)
	}

	static async getDestinations(
		profileId: string,
		type?: ProfileDestinationType
	): Promise<ProfileDestination[]> {
		return await ProfileRepository.getDestinations(profileId, type)
	}

	static async removeDestination(userId: string, profileDestinationId: string): Promise<void> {
		await ProfileRepository.removeDestination(userId, profileDestinationId)
	}

	static async getWishList(profileId: string): Promise<ProfileDestination[]> {
		return await ProfileRepository.getWishList(profileId)
	}

	static async getVisitedDestinations(
		profileId: string,
		hasItineraries?: boolean
	): Promise<ProfileDestination[]> {
		return await ProfileRepository.getVisitedDestinations(profileId, hasItineraries)
	}

	static async getDestinationsOld(
		profileId: string,
		type: 'wished' | 'visited'
	): Promise<ProfileDestination[]> {
		if (type === 'wished') return await ProfileRepository.getWishList(profileId)
		return await ProfileRepository.getVisitedDestinations(profileId)
	}

	static async getStats(profileId: string): Promise<ProfileStats> {
		return await ProfileRepository.getStats(profileId)
	}

	static async checkUsernameAvailability(username: string): Promise<boolean> {
		return await ProfileRepository.checkUsernameAvailability(username)
	}

	static async addWishlist(userId: string, wishlist: Array<string>): Promise<void> {
		await ProfileRepository.addWishlist(userId, wishlist)
	}

	static async addVisitedList(userId: string, visitedList: Array<string>): Promise<void> {
		await ProfileRepository.addVisitedList(userId, visitedList)
	}

	static async removeWishlistDestination(userId: string, destinationId: string): Promise<void> {
		await ProfileRepository.removeWishlistDestination(userId, destinationId)
	}

	static async removeVisitedDestination(userId: string, destinationId: string): Promise<void> {
		await ProfileRepository.removeVisitedDestination(userId, destinationId)
	}

	static async getLastUpdatedTrip(profileId: string): Promise<Itinerary2> {
		return await ProfileRepository.getLastUpdatedTrip(profileId)
	}

	static async validateProfile(
		profile: Profile
	): Promise<Partial<Record<keyof Profile, string>> | undefined> {
		const profileErrors: Partial<Record<keyof Profile, string>> = {}

		profileErrors.name = this.retrieveProfileNameErrors(profile)
		profileErrors.birthYear = this.retrieveYearErrors(profile)
		profileErrors.country = this.retrieveCountryErrors(profile)

		const hasErrors = Object.values(profileErrors).some((value) => Boolean(value))
		if (!hasErrors) return

		return profileErrors
	}

	static hasToCompleteProfile(profile: Profile): boolean {
		return profile && !profile.birthYear
	}

	static destinationToProfileDestinations(
		items: Destination2[],
		type: ProfileDestinationType
	): ProfileDestination[] {
		return items.map((item) => this.destinationToProfileDestination(item, type))
	}

	static destinationToProfileDestination(
		item: Destination2,
		type: ProfileDestinationType
	): ProfileDestination {
		return {
			destinationId: item.id!,
			type: type,
			name: item.name,
		}
	}

	private static retrieveProfileNameErrors(profile: Profile): string {
		if (!profile.name) return 'empty_value'
		if (!TextValidator.profileName(profile.name)) return 'invalid_format'
		return ''
	}

	private static retrieveYearErrors(profile: Profile): string {
		if (!profile.birthYear) return 'empty_value'
		const isValidYear: boolean = TextValidator.year(profile.birthYear)
		const isOutOfRange: boolean = !TextValidator.yearInRange(profile.birthYear)
		if (!isValidYear || isOutOfRange) return 'invalid_format'
		return ''
	}

	private static retrieveCountryErrors(profile: Profile) {
		if (!profile.country) return 'empty_value'
		return ''
	}
}
